import { ReactNode, useState } from "react";
import { useAdmin } from "@/context/AdminContext";

interface SectionVisibilityWrapperProps {
  sectionKey: string;
  label: string;
  children: ReactNode;
  isFirst?: boolean;
  isLast?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onAddSection?: () => void;
  onDuplicateSection?: () => void;
  onLabelChange?: (newLabel: string) => void;
}

export default function SectionVisibilityWrapper({
  sectionKey,
  label,
  children,
  isFirst = false,
  isLast = false,
  onMoveUp,
  onMoveDown,
  onAddSection,
  onDuplicateSection,
  onLabelChange,
}: SectionVisibilityWrapperProps) {
  const { isAdmin, isEditing, isSectionVisible, toggleSection, isPreviewMode } = useAdmin();
  const visible = isSectionVisible(sectionKey);
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelDraft, setLabelDraft] = useState(label);

  // Non-admin or preview mode: hide completely if not visible
  if ((!isAdmin || isPreviewMode) && !visible) return null;

  // Preview mode: render children as-is, no admin chrome
  if (isPreviewMode) {
    return <div id={sectionKey}>{children}</div>;
  }

  const handleLabelSave = () => {
    if (labelDraft.trim() && labelDraft !== label) {
      onLabelChange?.(labelDraft.trim());
    }
    setEditingLabel(false);
  };

  return (
    <div
      id={sectionKey}
      className={`relative group/section-wrapper transition-all duration-200`}
    >
      {children}

      {/* Admin overlay toolbar */}
      {isEditing && (
        <div className="absolute top-3 right-3 z-[50] flex items-center gap-1.5 opacity-0 group-hover/section-wrapper:opacity-100 transition-opacity">
          {/* Move up button */}
          <button
            type="button"
            onClick={onMoveUp}
            disabled={isFirst}
            title="Move section up"
            className={`flex items-center justify-center w-8 h-8 rounded-full cursor-pointer whitespace-nowrap transition-all border bg-white/90 border-gray-200 hover:bg-gray-50 text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed`}
          >
            <i className="ri-arrow-up-line text-sm"></i>
          </button>

          {/* Move down button */}
          <button
            type="button"
            onClick={onMoveDown}
            disabled={isLast}
            title="Move section down"
            className={`flex items-center justify-center w-8 h-8 rounded-full cursor-pointer whitespace-nowrap transition-all border bg-white/90 border-gray-200 hover:bg-gray-50 text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed`}
          >
            <i className="ri-arrow-down-line text-sm"></i>
          </button>

          {/* Duplicate section button */}
          {onDuplicateSection && (
            <button
              type="button"
              onClick={onDuplicateSection}
              title={`Duplicate "${label}" section`}
              className="flex items-center gap-1 text-xs font-bold px-2.5 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-all border bg-white/90 text-gray-600 border-gray-200 hover:bg-amber-50 hover:text-amber-700 hover:border-amber-200"
            >
              <i className="ri-file-copy-line text-sm"></i>
              <span className="hidden lg:inline">Duplicate</span>
            </button>
          )}

          {/* Add new section button */}
          {onAddSection && (
            <button
              type="button"
              onClick={onAddSection}
              title="Add a new section after this one"
              className="flex items-center gap-1 text-xs font-bold px-2.5 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-all border bg-white/90 text-gray-600 border-gray-200 hover:bg-green-50 hover:text-green-700 hover:border-green-200"
            >
              <i className="ri-add-line text-sm"></i>
              <span className="hidden lg:inline">Add Section</span>
            </button>
          )}

          {/* Visibility toggle */}
          <button
            type="button"
            onClick={() => toggleSection(sectionKey)}
            title={visible ? `Hide "${label}" from visitors` : `Show "${label}" to visitors`}
            className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-all border ${
              visible
                ? "bg-white/90 text-gray-700 border-gray-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                : "bg-red-500 text-white border-red-500 hover:bg-red-600"
            }`}
          >
            <i className={`${visible ? "ri-eye-line" : "ri-eye-off-line"} text-sm`}></i>
            {visible ? "Visible" : "Hidden"}
          </button>

          {/* Section label badge (editable) */}
          {editingLabel && onLabelChange ? (
            <div className="flex flex-col items-start gap-1">
              <input
                type="text"
                value={labelDraft}
                onChange={(e) => setLabelDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleLabelSave(); if (e.key === "Escape") { setEditingLabel(false); setLabelDraft(label); } }}
                onBlur={handleLabelSave}
                autoFocus
                className="w-32 text-xs font-semibold px-2 py-1 rounded border border-pink-300 bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-pink-400"
              />
              <span className="text-[10px] text-gray-500 bg-gray-900/80 px-1.5 py-0.5 rounded">
                Anchor: #{sectionKey}
              </span>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => { setEditingLabel(true); setLabelDraft(label); }}
              title={`Click to rename. Anchor link: #${sectionKey}`}
              className="flex flex-col items-start gap-0.5 text-xs font-semibold px-2.5 py-1.5 rounded-full bg-gray-900/80 text-gray-300 border border-gray-700 select-none hover:bg-gray-900 cursor-pointer"
            >
              <span className="flex items-center gap-1">
                {label}
                <i className="ri-edit-line text-[10px] opacity-60"></i>
              </span>
              <span className="text-[10px] text-gray-500 leading-none">
                #{sectionKey}
              </span>
            </button>
          )}
        </div>
      )}

      {/* Dim overlay when hidden (admin only) */}
      {isEditing && !visible && (
        <div className="absolute inset-0 bg-gray-900/40 pointer-events-none z-[40] flex items-center justify-center">
          <div className="bg-gray-900/80 text-white text-sm font-bold px-4 py-2 rounded-full flex items-center gap-2">
            <i className="ri-eye-off-line"></i>
            {label} — Hidden from visitors
          </div>
        </div>
      )}
    </div>
  );
}